import { NavLink } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button-variants";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NavItem } from "@/config/navigation";
import { NavNestedSubmenuGroup } from "./NavNestedSubmenuGroup";
import {
  formatNavBadgeCount,
  getNavIconClass,
  navLinkClass,
  shouldRenderCollapsedTooltip,
} from "./navButtonHelpers";

interface NavBadgeRowProps {
  showProBadge: boolean;
  badgeCount?: number;
  isMobile: boolean;
}

/**
 * Nav Badge Row.
 */
export function NavBadgeRow({ showProBadge, badgeCount, isMobile }: Readonly<NavBadgeRowProps>) {
  const formattedBadge = formatNavBadgeCount(badgeCount);

  return (
    <div className="flex items-center gap-1.5">
      {showProBadge ? (
        <span
          className={cn(
            "ml-2 inline-flex items-center text-[10px] text-primary/90 font-medium",
            isMobile ? "gap-0.5" : "bg-primary/10 px-1.5 py-0.5 rounded-md border border-primary/15"
          )}
          aria-label="Premium feature"
        >
          {isMobile ? "Premium" : "Pro"}
        </span>
      ) : null}
      {formattedBadge ? (
        <span className="ml-1 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-semibold">
          {formattedBadge}
        </span>
      ) : null}
    </div>
  );
}

interface NavSubmenuButtonProps {
  href: string;
  targetHref: string;
  icon: LucideIcon;
  label: string;
  isCollapsed: boolean;
  isMobile: boolean;
  isExpanded: boolean;
  showProBadge: boolean;
  badgeCount?: number;
  submenu: NavItem[];
  onClick?: () => void;
  onPrefetch: () => void;
  onToggleExpanded: () => void;
}

/**
 * Nav Submenu Button.
 */
export function NavSubmenuButton({
  href,
  targetHref,
  icon: Icon,
  label,
  isCollapsed,
  isMobile,
  isExpanded,
  showProBadge,
  badgeCount,
  submenu,
  onClick,
  onPrefetch,
  onToggleExpanded,
}: Readonly<NavSubmenuButtonProps>) {
  const badgeRow = (
    <NavBadgeRow showProBadge={showProBadge} badgeCount={badgeCount} isMobile={isMobile} />
  );

  return (
    <div className="w-full">
      <div className="flex items-center w-full">
        <NavLink
          to={targetHref}
          onMouseEnter={onPrefetch}
          onFocus={onPrefetch}
          onClick={() => {
            onToggleExpanded();
            onClick?.();
          }}
          className={({ isActive }) =>
            cn(
              "flex flex-1 items-center rounded-lg",
              navLinkClass({ isActive, isMobile, isCollapsed, isExpanded, targetHref, href })
            )
          }
        >
          <Icon
            className={getNavIconClass({
              isMobile,
              isCollapsed,
              isActive: false,
              targetHref,
              href,
              isExpanded,
            })}
          />
          <span className="truncate flex-1 text-left">{label}</span>
          {badgeRow}
        </NavLink>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggleExpanded();
          }}
          aria-label={isExpanded ? "Collapse submenu" : "Expand submenu"}
          aria-expanded={isExpanded}
          className={cn(
            buttonVariants({ variant: "ghost", size: "icon" }),
            "h-9 w-9 p-0 flex-shrink-0",
            isExpanded && "text-primary"
          )}
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              isExpanded && "transform rotate-180"
            )}
          />
        </button>
      </div>
      {isExpanded ? (
        <div className="ml-4 mt-2 space-y-2 border-l border-border/50 pl-4">
          {submenu.map((item) => (
            <NavNestedSubmenuGroup
              key={item.href}
              item={item}
              isCollapsed={isCollapsed}
              isMobile={isMobile}
              onClick={onClick}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface NavSimpleLinkProps {
  href: string;
  targetHref: string;
  icon: LucideIcon;
  label: string;
  isCollapsed: boolean;
  isMobile: boolean;
  showProBadge: boolean;
  badgeCount?: number;
  tooltipContent: string;
  onClick?: () => void;
  onPrefetch: () => void;
}

/**
 * Nav Simple Link.
 */
export function NavSimpleLink({
  href,
  targetHref,
  icon: Icon,
  label,
  isCollapsed,
  isMobile,
  showProBadge,
  badgeCount,
  tooltipContent,
  onClick,
  onPrefetch,
}: Readonly<NavSimpleLinkProps>) {
  const link = (
    <NavLink
      to={targetHref}
      onMouseEnter={onPrefetch}
      onFocus={onPrefetch}
      onClick={onClick}
      aria-label={isCollapsed && !isMobile ? tooltipContent : undefined}
      className={({ isActive }) =>
        cn(
          "flex w-full items-center rounded-lg",
          navLinkClass({ isActive, isMobile, isCollapsed, targetHref, href })
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={getNavIconClass({
              isMobile,
              isCollapsed,
              isActive,
              targetHref,
              href,
            })}
          />
          {(!isCollapsed || isMobile) && (
            <div className="flex min-w-0 flex-1 items-center justify-between">
              <span className="truncate">{label}</span>
              <NavBadgeRow
                showProBadge={showProBadge}
                badgeCount={badgeCount}
                isMobile={isMobile}
              />
            </div>
          )}
        </>
      )}
    </NavLink>
  );

  if (!shouldRenderCollapsedTooltip(isCollapsed, isMobile)) {
    return link;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent
          side="right"
          className="bg-popover text-popover-foreground border border-border"
        >
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
